import React from 'react';
import { Avatar, Box, CircularProgress, Typography } from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { AgentActionButton } from '../../types/agent';
import type { AgentSwaggerImportStep } from '../../utils/agentSwaggerImportProgress';
import AgentExecutionResultMessage, { parseAgentExecutionResult } from '../AgentExecutionResultMessage';
import { normalizeAgentChatMarkdown } from '../../utils/agentChatMarkdown';
import { AI_AGENT_UI } from './tokens';

export type AgentUiChatMessage = {
  id?: number;
  role: 'user' | 'agent';
  text: string;
  actions?: AgentActionButton[];
  isFailure?: boolean;
  isSuccess?: boolean;
};

type Props = {
  messages: AgentUiChatMessage[];
  loading?: boolean;
  loadingHistory?: boolean;
  loadingOlder?: boolean;
  hasMoreOlder?: boolean;
  formatActionLabel: (id: string, label: string) => string;
  onAction: (action: AgentActionButton) => void;
  isFailureMessage: (m: AgentUiChatMessage) => boolean;
  isSuccessMessage: (m: AgentUiChatMessage) => boolean;
  resolveImportProgress?: (m: AgentUiChatMessage) => AgentSwaggerImportStep[] | null | undefined;
  renderImportProgress?: (steps: AgentSwaggerImportStep[]) => React.ReactNode;
  swaggerImportSteps?: AgentSwaggerImportStep[] | null;
  endRef?: React.RefObject<HTMLDivElement | null>;
};

function ActionLinks({
  actions,
  onAction,
  formatActionLabel,
  tone = 'default',
}: {
  actions: AgentActionButton[];
  onAction: (action: AgentActionButton) => void;
  formatActionLabel: (id: string, label: string) => string;
  tone?: 'default' | 'success' | 'error';
}) {
  if (!actions.length) return null;
  const chipSx =
    tone === 'success'
      ? {
          border: `1px solid ${AI_AGENT_UI.successBorder}`,
          bgcolor: '#fff',
          color: AI_AGENT_UI.success,
          '&:hover': { bgcolor: AI_AGENT_UI.successMuted },
        }
      : tone === 'error'
        ? {
            border: `1px solid ${AI_AGENT_UI.errorBorder}`,
            bgcolor: '#fff',
            color: AI_AGENT_UI.error,
            '&:hover': { bgcolor: AI_AGENT_UI.errorMuted },
          }
        : {
            border: `1px solid ${AI_AGENT_UI.border}`,
            bgcolor: AI_AGENT_UI.surface,
            color: AI_AGENT_UI.primary,
            '&:hover': { bgcolor: 'rgba(29, 78, 216, 0.06)' },
          };

  return (
    <Box sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {actions.map((a, i) => (
        <Box
          key={`${a.id}-${i}`}
          component="button"
          type="button"
          onClick={() => onAction(a)}
          sx={{
            borderRadius: `${AI_AGENT_UI.radius.pill}px`,
            px: 1.25,
            py: 0.4,
            cursor: 'pointer',
            fontSize: AI_AGENT_UI.fontSm,
            fontWeight: 600,
            ...chipSx,
          }}
        >
          {formatActionLabel(a.id, a.label)}
        </Box>
      ))}
    </Box>
  );
}

function AgentMarkdownBody({
  text,
  color,
}: {
  text: string;
  color: string;
}) {
  return (
    <Box
      sx={{
        fontSize: AI_AGENT_UI.font,
        fontWeight: 400,
        lineHeight: AI_AGENT_UI.lineHeight,
        color,
        wordBreak: 'break-word',
        '& p': { m: 0, mb: 0.75 },
        '& p:last-child': { mb: 0 },
        '& ul, & ol': { mt: 0.35, mb: 0.75, pl: 2.1 },
        '& li': { mb: 0.35 },
        '& li > p': { display: 'inline', m: 0 },
        '& li p': { m: 0 },
        '& strong': { fontWeight: 700, color },
        '& hr': { my: 1, border: 0, borderTop: `1px solid ${AI_AGENT_UI.border}` },
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{normalizeAgentChatMarkdown(text)}</ReactMarkdown>
    </Box>
  );
}

function MessageRow({
  avatar,
  children,
}: {
  avatar: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        width: '100%',
        maxWidth: AI_AGENT_UI.bubbleMaxWidth + 44,
        minWidth: 0,
      }}
    >
      {avatar}
      <Box sx={{ flex: '0 1 auto', minWidth: 0, maxWidth: AI_AGENT_UI.bubbleMaxWidth }}>{children}</Box>
    </Box>
  );
}

/** Script-style chat thread — AI Agent page only. */
export function AgentChatThread({
  messages,
  loading,
  loadingHistory,
  loadingOlder,
  hasMoreOlder,
  formatActionLabel,
  onAction,
  isFailureMessage,
  isSuccessMessage,
  resolveImportProgress,
  renderImportProgress,
  swaggerImportSteps,
  endRef,
}: Props) {
  if (loadingHistory) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        width: '100%',
        maxWidth: AI_AGENT_UI.contentMaxWidth,
        minWidth: 0,
        mx: 'auto',
      }}
    >
      {(loadingOlder || hasMoreOlder) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.75 }}>
          {loadingOlder ? (
            <CircularProgress size={16} />
          ) : (
            <Typography sx={{ fontSize: AI_AGENT_UI.fontSm, color: AI_AGENT_UI.textSecondary }}>
              Scroll up for older messages
            </Typography>
          )}
        </Box>
      )}

      {messages.map((m, idx) => {
        const failure = isFailureMessage(m);
        const success = !failure && isSuccessMessage(m);
        const isUser = m.role === 'user';
        const importProgress = resolveImportProgress?.(m);
        const structured = !isUser && parseAgentExecutionResult(m.text) != null;

        if (isUser) {
          return (
            <MessageRow
              key={m.id ?? `local-${idx}`}
              avatar={
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: AI_AGENT_UI.surfaceMuted,
                    color: AI_AGENT_UI.textSecondary,
                    border: `1px solid ${AI_AGENT_UI.border}`,
                  }}
                >
                  <PersonOutlineIcon sx={{ fontSize: 18 }} />
                </Avatar>
              }
            >
              <Box
                sx={{
                  display: 'inline-block',
                  maxWidth: '100%',
                  width: 'fit-content',
                  px: 1.5,
                  py: 1,
                  borderRadius: `${AI_AGENT_UI.radius.lg}px`,
                  bgcolor: AI_AGENT_UI.surface,
                  border: `1px solid ${AI_AGENT_UI.border}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: AI_AGENT_UI.font,
                    fontWeight: 500,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: AI_AGENT_UI.lineHeight,
                    color: AI_AGENT_UI.text,
                  }}
                >
                  {m.text}
                </Typography>
              </Box>
            </MessageRow>
          );
        }

        return (
          <MessageRow
            key={m.id ?? `local-${idx}`}
            avatar={
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: AI_AGENT_UI.navy,
                  color: '#fff',
                }}
              >
                <SmartToyOutlinedIcon sx={{ fontSize: 18 }} />
              </Avatar>
            }
          >
            <Box
              sx={{
                display: 'inline-block',
                width: 'fit-content',
                maxWidth: '100%',
                px: 1.5,
                py: 1.25,
                borderRadius: `${AI_AGENT_UI.radius.lg}px`,
                bgcolor: failure
                  ? AI_AGENT_UI.errorMuted
                  : success
                    ? AI_AGENT_UI.successMuted
                    : AI_AGENT_UI.surfaceMuted,
                border: failure
                  ? `1.5px solid ${AI_AGENT_UI.errorBorder}`
                  : success
                    ? `1.5px solid ${AI_AGENT_UI.successBorder}`
                    : '1px solid transparent',
              }}
            >
              {structured ? (
                <Box sx={{ fontSize: AI_AGENT_UI.font, '& *': { fontSize: 'inherit !important' } }}>
                  <AgentExecutionResultMessage text={m.text} failure={failure} success={success} />
                </Box>
              ) : (
                <AgentMarkdownBody
                  text={m.text}
                  color={
                    failure
                      ? AI_AGENT_UI.error
                      : success
                        ? AI_AGENT_UI.success
                        : AI_AGENT_UI.text
                  }
                />
              )}
              {importProgress && renderImportProgress && (
                <Box sx={{ mt: 1.25 }}>{renderImportProgress(importProgress)}</Box>
              )}
              <ActionLinks
                actions={m.actions || []}
                onAction={onAction}
                formatActionLabel={formatActionLabel}
                tone={failure ? 'error' : success ? 'success' : 'default'}
              />
            </Box>
          </MessageRow>
        );
      })}

      {loading && (
        <MessageRow
          avatar={
            <Avatar sx={{ width: 32, height: 32, bgcolor: AI_AGENT_UI.navy, color: '#fff' }}>
              <SmartToyOutlinedIcon sx={{ fontSize: 18 }} />
            </Avatar>
          }
        >
          <Box
            sx={{
              width: 'fit-content',
              maxWidth: '100%',
              px: 1.5,
              py: 1.25,
              borderRadius: `${AI_AGENT_UI.radius.lg}px`,
              bgcolor: AI_AGENT_UI.surfaceMuted,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} />
              <Typography sx={{ fontSize: AI_AGENT_UI.font, color: AI_AGENT_UI.textSecondary }}>
                Working on your request…
              </Typography>
            </Box>
            {swaggerImportSteps && renderImportProgress && renderImportProgress(swaggerImportSteps)}
          </Box>
        </MessageRow>
      )}

      <div ref={endRef as React.RefObject<HTMLDivElement>} />
    </Box>
  );
}
